import React,{useState} from 'react';
import {motion,Reorder} from 'framer-motion';
import SafeIcon from '../SafeIcon';
import {FiX,FiPlus,FiTrash2,FiLink,FiMenu,FiCheck,FiAlertCircle} from 'react-icons/fi';

const MenuPopup=({onClose,menuItems,onUpdate,availableAnchors=[],pages=[]})=> {
  // Initialize items,ensuring linkType is set based on current link if missing
  const [items,setItems]=useState(
    menuItems.map(item=> ({
      ...item,
      linkType: item.linkType || (item.link?.startsWith('#') ? 'anchor' : 'url')
    }))
  );

  const addItem=()=> {
    const newItem={id: Date.now(),label: 'New Link',link: '',linkType: 'url'};
    setItems([...items,newItem]);
  };

  const removeItem=(id)=> setItems(items.filter(item=> item.id !==id));

  // Update multiple fields at once to prevent state race conditions
  const updateItem=(id,updates)=> {
    setItems(items.map(item=> (item.id===id ? {...item,...updates} : item)));
  };

  const saveAndClose=()=> {
    onUpdate(items);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <motion.div
        initial={{opacity: 0,scale: 0.95,y: 10}}
        animate={{opacity: 1,scale: 1,y: 0}}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]"
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/80">
          <div>
            <h3 className="font-black text-gray-900 text-xl tracking-tight">Navigation Menu</h3>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Manage your website links and their order</p>
          </div>
          <button onClick={onClose} className="p-2.5 bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50 rounded-xl text-gray-400 transition-all shadow-sm">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        {/* Content Area */}
        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar bg-[#f8f9fa]">
          <div className="space-y-4 mb-6">
            <Reorder.Group
              axis="y"
              values={items}
              onReorder={setItems}
              className="space-y-4"
            >
              {items.map((item)=> (
                <Reorder.Item
                  key={item.id}
                  value={item}
                  className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm relative group flex gap-4"
                  dragListener={false}
                >
                  {/* Drag handle */}
                  <div className="pt-2 cursor-grab active:cursor-grabbing text-gray-300 group-hover:text-gray-500 touch-none transition-colors">
                    <SafeIcon icon={FiMenu} className="w-5 h-5" />
                  </div>

                  <div className="flex-1 space-y-5">
                    {/* Top Row: Label and Delete */}
                    <div className="flex gap-4 items-start">
                      <div className="flex-1">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5">Navigation Label</label>
                        <input
                          type="text"
                          value={item.label}
                          onChange={(e)=> updateItem(item.id,{label: e.target.value})}
                          className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm font-bold text-gray-800 outline-none focus:ring-2 focus:ring-teal-400/20 focus:border-teal-400 focus:bg-white transition-all"
                          placeholder="e.g. About Us"
                        />
                      </div>
                      <button
                        onClick={()=> removeItem(item.id)}
                        className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors mt-6 border border-transparent hover:border-red-100"
                        title="Remove Link"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Link Configuration Box */}
                    <div className="bg-gray-50/80 rounded-xl p-4 border border-gray-100 space-y-4">
                      <div className="flex items-center justify-between">
                        <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Link Destination</label>
                        {/* Segmented Toggle for Link Type */}
                        <div className="flex bg-gray-200/60 p-1 rounded-lg">
                          <button
                            onClick={()=> updateItem(item.id,{linkType: 'url',link: ''})}
                            className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${
                              item.linkType==='url'
                                ? 'bg-white text-teal-600 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            URL
                          </button>
                          <button
                            onClick={()=> updateItem(item.id,{linkType: 'page',link: ''})}
                            className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${
                              item.linkType==='page'
                                ? 'bg-white text-teal-600 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            Page
                          </button>
                          <button
                            onClick={()=> updateItem(item.id,{linkType: 'anchor',link: ''})}
                            className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${
                              item.linkType==='anchor'
                                ? 'bg-white text-teal-600 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            Section Anchor
                          </button>
                        </div>
                      </div>

                      {/* Input based on type */}
                      <div>
                        {item.linkType==='page' ? (
                          pages.length > 0 ? (
                            <div className="relative">
                              <select
                                value={item.link || ''}
                                onChange={(e)=> updateItem(item.id,{link: e.target.value})}
                                className="w-full bg-white border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-teal-400/20 focus:border-teal-400 cursor-pointer text-gray-800 font-medium appearance-none"
                              >
                                <option value="">Select a saved page...</option>
                                {pages.map(page=> (
                                  <option key={page.id} value={page.slug}>
                                    {page.name}
                                  </option>
                                ))}
                              </select>
                              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </div>
                            </div>
                          ) : (
                            <div className="w-full bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs font-medium text-amber-700 flex items-center">
                              <SafeIcon icon={FiAlertCircle} className="w-4 h-4 mr-2.5 shrink-0" />
                              <span>No pages created yet. Create a page to link to it.</span>
                            </div>
                          )
                        ) : item.linkType==='anchor' ? (
                          availableAnchors.length > 0 ? (
                            <div className="relative">
                              <select
                                value={item.link?.replace('#','') || ''}
                                onChange={(e)=> updateItem(item.id,{link: e.target.value ? `#${e.target.value}` : ''})}
                                className="w-full bg-white border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-teal-400/20 focus:border-teal-400 cursor-pointer text-gray-800 font-medium appearance-none"
                              >
                                <option value="">Select an anchor on this page...</option>
                                {availableAnchors.map(a=> (
                                  <option key={a} value={a}>{a}</option>
                                ))}
                              </select>
                              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </div>
                            </div>
                          ) : (
                            <div className="w-full bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs font-medium text-amber-700 flex items-center">
                              <SafeIcon icon={FiAlertCircle} className="w-4 h-4 mr-2.5 shrink-0" />
                              <span>No Section Anchors found on this page. Edit a Section's settings to add an Anchor Point ID first.</span>
                            </div>
                          )
                        ) : (
                          <div className="relative">
                            <SafeIcon icon={FiLink} className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              value={item.link}
                              onChange={(e)=> updateItem(item.id,{link: e.target.value})}
                              className="w-full bg-white border border-gray-200 rounded-lg pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-teal-400/20 focus:border-teal-400 focus:bg-white transition-all font-medium text-gray-800"
                              placeholder="/contact or https://example.com"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Reorder.Item>
              ))}
            </Reorder.Group>

            <button
              onClick={addItem}
              className="w-full py-4 border-2 border-dashed border-gray-200 rounded-xl text-gray-500 font-bold hover:border-teal-400 hover:text-teal-600 hover:bg-teal-50 transition-all flex items-center justify-center space-x-2 bg-white shadow-sm"
            >
              <SafeIcon icon={FiPlus} className="w-5 h-5" />
              <span>Add Navigation Link</span>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 bg-white flex space-x-4 shrink-0">
          <button
            onClick={onClose}
            className="flex-1 py-3.5 text-gray-500 font-bold hover:bg-gray-100 rounded-xl transition-colors"
          >
            Discard
          </button>
          <button
            onClick={saveAndClose}
            className="flex-2 px-10 py-3.5 bg-teal-500 text-white font-bold rounded-xl shadow-lg shadow-teal-100 hover:bg-teal-600 transition-all flex items-center justify-center space-x-2"
          >
            <SafeIcon icon={FiCheck} className="w-5 h-5" />
            <span>Apply Changes</span>
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default MenuPopup;